/**
 * Canonical metadata for every simulation_setting key. Both the validator
 * (api/librarySettingsUpdate) and the frontend authoring UI render from this.
 *
 * `active = true` ⇒ at least one runtime code path reads this key today.
 *   - MaxAttempts                (attempt-start, simulations list)
 *   - TimerEnabled               (tree.settings → AttemptPlayerPage countdown)
 *   - TimerMinutes               (same)
 *   - LeaderboardEnabled         (sim-leaderboard gate, simulations list)
 *   - ReflectionScreenRequired   (attempt-results)
 *   - DefaultMinOpenTextLength   (AttemptPlayerPage — sim-wide fallback when
 *                                 a node has no per-node min_open_text_length)
 *   - ReflectionMinChars         (ReflectionPage — gates Continue button)
 *   - ResumeEnabled              (attempt-start — when "No", any in_progress
 *                                 attempt is force-abandoned and a fresh one
 *                                 created)
 *   - SaveProgressEnabled        (attempt-respond — when "No", skip the
 *                                 current_node_id UPDATE so closing the
 *                                 browser mid-attempt forfeits the run)
 *   - EnableAntiCheat            (frontend useAntiCheat — per-sim toggle on
 *                                 top of the global VITE_ANTICHEAT_ENABLED
 *                                 kill switch; both must be ON for blocking)
 *
 * Everything else is a Power-Apps-era shell. Authors can set values; the
 * runtime ignores them until we explicitly wire them.
 */

export type SimSettingType = "bool" | "number" | "enum";

export type SimSettingGroup =
  | "attempts"
  | "display"
  | "flow"
  | "notifications"
  | "misc";

export interface SimSettingDef {
  key: string;
  label: string;
  hint?: string;
  type: SimSettingType;
  group: SimSettingGroup;
  active: boolean;
  /** For enum types: the allowed values (case-sensitive). */
  options?: string[];
  /** For number types: range. Validated server-side. */
  min?: number;
  max?: number;
}

/**
 * Booleans are stored as the literal strings "Yes" / "No" to stay
 * compatible with the Power Apps spec and the Excel import format.
 */
export const SIM_SETTING_DEFS: SimSettingDef[] = [
  // ─── Attempts & Limits ───
  { key: "MaxAttempts", label: "Max attempts per learner", type: "number", group: "attempts", active: true, min: 1, max: 99 },
  { key: "TimerEnabled", label: "Enable whole-attempt timer", type: "bool", group: "attempts", active: true },
  { key: "TimerMinutes", label: "Timer (minutes)", type: "number", group: "attempts", active: true, min: 1, max: 240, hint: "Only used when timer is enabled" },
  { key: "DefaultMinOpenTextLength", label: "Default minimum open-text answer length (chars)", type: "number", group: "attempts", active: true, min: 0, max: 2000, hint: "Used by open-text decisions that don't override it per node" },
  { key: "AllowBackNavigation", label: "Allow back navigation", type: "bool", group: "attempts", active: false },
  { key: "ResumeEnabled", label: "Resume in-progress attempt", type: "bool", group: "attempts", active: true, hint: "When off, any in-progress attempt is force-abandoned on the next start; learner restarts fresh." },
  { key: "SaveProgressEnabled", label: "Save progress between decisions", type: "bool", group: "attempts", active: true, hint: "When off, server doesn't track current position between decisions; closing the browser mid-attempt forfeits the run." },
  { key: "EnableAntiCheat", label: "Block paste / copy / right-click in open-text", type: "bool", group: "attempts", active: true, hint: "Per-sim toggle on top of the global VITE_ANTICHEAT_ENABLED kill switch." },

  // ─── Display & Feedback ───
  { key: "LeaderboardEnabled", label: "Show leaderboard after attempt", type: "bool", group: "display", active: true },
  { key: "ReflectionScreenRequired", label: "Require reflection before coaching", type: "bool", group: "display", active: true },
  { key: "ReflectionMinChars", label: "Minimum reflection length (chars)", type: "number", group: "display", active: true, min: 0, max: 2000, hint: "0 = no minimum. Continue button stays disabled until the learner reaches this length." },
  { key: "ShowScoresDuringSimulation", label: "Show score live during play", type: "bool", group: "display", active: false },
  { key: "ShowCorrectAnswers", label: "Reveal correct answer", type: "bool", group: "display", active: false },
  { key: "FeedbackEnabled", label: "Show feedback after each decision", type: "bool", group: "display", active: false },
  { key: "FeedbackTiming", label: "Feedback timing", type: "enum", group: "display", active: false, options: ["Immediate", "End"] },
  { key: "ConsequenceTextDisplay", label: "Show consequence text", type: "enum", group: "display", active: false, options: ["Always", "OnHighScore", "Never"] },

  // ─── Question flow ───
  { key: "RandomizeChoices", label: "Randomise choice order (MC)", type: "bool", group: "flow", active: false },
  { key: "RequireAllNodes", label: "Require all nodes to be visited", type: "bool", group: "flow", active: false },
  { key: "PassingScore", label: "Passing score (0-100)", type: "number", group: "flow", active: false, min: 0, max: 100 },
  { key: "AdaptiveDifficulty", label: "Adapt difficulty to performance", type: "bool", group: "flow", active: false },

  // ─── Notifications & sharing ───
  { key: "CertificateEnabled", label: "Issue certificate on pass", type: "bool", group: "notifications", active: false },
  { key: "NotificationsEnabled", label: "Send email notifications", type: "bool", group: "notifications", active: false },
  { key: "NotifyOnComplete", label: "Email on completion", type: "bool", group: "notifications", active: false },
  { key: "NotifyOnFail", label: "Email on failure", type: "bool", group: "notifications", active: false },
  { key: "ManagerVisibility", label: "Manager can view scores", type: "bool", group: "notifications", active: false },
  { key: "PeerComparisonEnabled", label: "Show peer comparison", type: "bool", group: "notifications", active: false },
  { key: "ExportEnabled", label: "Allow data export", type: "bool", group: "notifications", active: false },
  { key: "AnonymousMode", label: "Anonymise leaderboard names", type: "bool", group: "notifications", active: false },

  // ─── Misc ───
  { key: "MultiLanguageSupport", label: "Multi-language support", type: "bool", group: "misc", active: false },
];

export const SIM_SETTING_DEFS_BY_KEY: Record<string, SimSettingDef> = Object.fromEntries(
  SIM_SETTING_DEFS.map((d) => [d.key, d]),
);

/**
 * Coerce + validate one (key, value) pair. Returns a normalized string the
 * caller can persist verbatim, or throws Error with a learner-readable
 * message.
 */
export function validateSettingValue(key: string, value: unknown): string {
  const def = SIM_SETTING_DEFS_BY_KEY[key];
  if (def === undefined) throw new Error(`Unknown setting key: ${key}`);
  if (def.type === "bool") {
    if (value === true || value === "Yes" || value === "yes" || value === 1) return "Yes";
    if (value === false || value === "No" || value === "no" || value === 0) return "No";
    throw new Error(`${key} must be Yes or No`);
  }
  if (def.type === "number") {
    const n = Number(value);
    if (!Number.isFinite(n)) throw new Error(`${key} must be a number`);
    const rounded = Math.round(n);
    const min = def.min ?? Number.NEGATIVE_INFINITY;
    const max = def.max ?? Number.POSITIVE_INFINITY;
    if (rounded < min || rounded > max) {
      throw new Error(`${key} must be between ${min} and ${max}`);
    }
    return String(rounded);
  }
  // enum
  const v = String(value);
  if (def.options === undefined || !def.options.includes(v)) {
    throw new Error(`${key} must be one of: ${def.options?.join(", ") ?? ""}`);
  }
  return v;
}
